import * as React from "react"
import { cn } from "@/shared/shadcn/lib/utils"
import { Button } from "./Button"
import { Calendar } from "./calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip"

const defaultCalendarIcon = (
  <img src="/images/img_union_gray_600_01_14x14.svg" alt="calendar" className="w-3.5 h-3.5" />
)

interface DatePickerProps {
  date?: Date
  onDateChange?: (date: Date | undefined) => void
  placeholder?: string
  className?: string
  autoClose?: boolean
  allowManualInput?: boolean
  displayFormat?: 'MM/DD/YYYY'
  onValidationChange?: (isValid: boolean, message?: string) => void
  allowFutureDates?: boolean
  /** Optional custom calendar trigger icon. Falls back to the default union svg. */
  icon?: React.ReactNode
}

export function DatePicker({
  date,
  onDateChange,
  placeholder = "Pick a date",
  className,
  autoClose = false,
  allowManualInput = true,
  displayFormat = 'MM/DD/YYYY',
  onValidationChange,
  allowFutureDates = false,
  icon
}: DatePickerProps) {
  const calendarIcon = icon ?? defaultCalendarIcon
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [validationMessage, setValidationMessage] = React.useState("")

  // Set today as default date if no date is provided
  const defaultDate = date || new Date()
  const minDate = new Date(1900, 0, 1)

  const getToday = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  }

  const formatDateForDisplay = (selectedDate: Date) => {
    if (displayFormat === 'MM/DD/YYYY') {
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0')
      const day = String(selectedDate.getDate()).padStart(2, '0')
      const year = String(selectedDate.getFullYear())
      return `${month}/${day}/${year}`
    }
    return selectedDate.toLocaleDateString()
  }

  const emitValidation = (isValid: boolean, message = "") => {
    setValidationMessage(isValid ? "" : message)
    onValidationChange?.(isValid, isValid ? undefined : message)
  }

  const sanitizeManualDateInput = (value: string) => {
    const cleaned = value.replace(/[^\d/]/g, '')
    return cleaned.slice(0, 10)
  }

  const parseManualDate = (value: string): { date?: Date; error?: string } => {
    const fullDateMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (!fullDateMatch) {
      return { error: "Use MM/DD/YYYY format" }
    }

    const month = Number(fullDateMatch[1])
    const day = Number(fullDateMatch[2])
    const year = Number(fullDateMatch[3])

    if (month < 1 || month > 12) {
      return { error: "Month must be between 01 and 12" }
    }

    if (day < 1 || day > 31) {
      return { error: "Day must be between 01 and 31" }
    }

    const parsedDate = new Date(year, month - 1, day)
    if (
      parsedDate.getFullYear() !== year ||
      parsedDate.getMonth() !== month - 1 ||
      parsedDate.getDate() !== day
    ) {
      return { error: "Enter a valid calendar date" }
    }

    parsedDate.setHours(0, 0, 0, 0)
    const today = getToday()

    if (parsedDate < minDate) {
      return { error: "Date must be after 01/01/1900" }
    }
    
    if (!allowFutureDates && parsedDate > today) {
      return { error: "Future date is not allowed" }
    }

    return { date: parsedDate }
  }

  React.useEffect(() => {
    if (!allowManualInput) return
    if (!date) {
      setInputValue("")
      emitValidation(true)
      return
    }

    setInputValue(formatDateForDisplay(date))
    emitValidation(true)
  }, [date, allowManualInput, displayFormat])
  
  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (allowManualInput) {
      if (selectedDate) {
        setInputValue(formatDateForDisplay(selectedDate))
      } else {
        setInputValue("")
      }
      emitValidation(true)
    }

    onDateChange?.(selectedDate)
    if (autoClose && selectedDate) {
      setOpen(false)
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (allowManualInput) {
      setInputValue("")
      emitValidation(true)
    }
    onDateChange?.(undefined)
  }

  const handleManualInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeManualDateInput(event.target.value)
    setInputValue(sanitizedValue)

    // Do not validate/commit while typing to avoid cursor jumping and noisy UX.
    emitValidation(true)
  }

  const handleManualInputBlur = () => {
    commitManualInput()
  }

  const commitManualInput = () => {
    if (!inputValue) {
      emitValidation(true)
      onDateChange?.(undefined)
      return false
    }

    const digitsLength = inputValue.replace(/\D/g, '').length
    if (digitsLength < 8) {
      emitValidation(false, "Use MM/DD/YYYY format")
      return false
    }

    const parsed = parseManualDate(inputValue)
    if (!parsed.date) {
      emitValidation(false, parsed.error || "Enter a valid date")
      return false
    }

    setInputValue(formatDateForDisplay(parsed.date))
    emitValidation(true)
    onDateChange?.(parsed.date)
    return true
  }

  const handleManualInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      commitManualInput()
    }
  }
  
  if (allowManualInput) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <div
          className={cn(
            "bg-white w-full justify-between text-left font-normal bg-transparent h-auto p-0 ring-0 focus-within:ring-0 focus-within:ring-offset-0 flex items-center gap-1",
            className
          )}
        >
          <input
            type="text"
            value={inputValue}
            onChange={handleManualInputChange}
            onBlur={handleManualInputBlur}
            onKeyDown={handleManualInputKeyDown}
            inputMode="numeric"
            placeholder={displayFormat === 'MM/DD/YYYY' ? 'DOB (MM/DD/YYYY)' : placeholder}
            aria-invalid={Boolean(validationMessage)}
            className={cn(
              "text-sm font-normal leading-[17px] text-secondary-text flex-1 bg-transparent border-none outline-none p-0 min-w-0 focus-visible:outline-none",
              validationMessage && "text-red-600"
            )}
          />
          <div className="flex items-center gap-1">
            {validationMessage && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-100 text-red-600 text-[10px] font-bold cursor-help"
                      aria-label="Date validation error"
                    >
                      !
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs z-[10000]">
                    {validationMessage}
                    <TooltipArrow />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {inputValue && (
              <img
                src="/images/img_union_gray_600_01.svg"
                alt="clear"
                className="w-3.5 h-3.5 cursor-pointer hover:opacity-70 transition-opacity"
                onClick={handleClear}
              />
            )}
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center justify-center hover:opacity-70 transition-opacity"
                aria-label="Open calendar"
              >
                {calendarIcon}
              </button>
            </PopoverTrigger>
          </div>
        </div>
        <PopoverContent className="w-auto p-0 z-[110]" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            defaultMonth={defaultDate}
            disabled={(dayDate) => {
              if (allowFutureDates) {
                return dayDate < new Date("1900-01-01")
              }
              return dayDate > new Date() || dayDate < new Date("1900-01-01")
            }}
            captionLayout="dropdown"
            initialFocus
          />
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn( 
            "bg-white w-full justify-between text-left font-normal bg-transparent h-auto p-0 ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 hover:bg-transparent",
            !date && "text-secondary-text",
            className
          )}
        >
          <span className="text-sm font-normal leading-[17px] text-secondary-text flex-1">
            {date ? formatDateForDisplay(date) : placeholder}
          </span>
          <div className="flex items-center gap-1">
            {date && (
              <img 
                src="/images/img_union_gray_600_01.svg" 
                alt="clear" 
                className="w-3.5 h-3.5 cursor-pointer hover:opacity-70 transition-opacity" 
                onClick={handleClear}
              />
            )}
            {calendarIcon}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[110]" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleDateSelect}
          defaultMonth={defaultDate}
          disabled={(calendarDate) => {
            if (allowFutureDates) {
              return calendarDate < new Date("1900-01-01")
            }
            return calendarDate > new Date() || calendarDate < new Date("1900-01-01")
          }}
          captionLayout="dropdown"
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}